import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card } from "design-system/molecules/Card"
import { usePredefinedSegmentGroupsTranslation } from "modules/segments/hooks/usePredefinedSegmentGroupsTranslation"
import { useSegmentGroupCompareQuery } from "modules/segments/hooks/useSegmentGroupCompareQuery"
import { useSegmentGroupQuery } from "modules/segments/hooks/useSegmentGroupQuery"
import { useSegmentGroupsQuery } from "modules/segments/hooks/useSegmentGroupsQuery"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { PredefinedSegmentGroups } from "schema"
import { RfmGrid } from "./RfmGrid"
import { SegmentGroupChart } from "./SegmentGroupChart"
import { SegmentGroupCompareSelect } from "./SegmentGroupCompareSelect"
import { SegmentGroupMetrics } from "./SegmentGroupMetrics"
import { SegmentsTable } from "./SegmentsTable"
import { VisualizationSelect } from "./VisualizationSelect"

const NONE_VALUE = "none"

type Props = {
  segmentGroupId: string
  compareId?: string
  view: "chart" | "table"
  onChangeView: (view: "chart" | "table") => void
  onChangeCompareId: (compareId: string | undefined) => void
}

export function SegmentGroupDetail({
  segmentGroupId,
  compareId,
  view,
  onChangeView,
  onChangeCompareId,
}: Props) {
  const { t } = useTranslation()

  const { data: segmentGroup, isPending } = useSegmentGroupQuery({
    id: segmentGroupId,
  })
  const { data: segmentGroupCompare } = useSegmentGroupCompareQuery({
    id: segmentGroupId,
    compareId: compareId ?? "",
    enabled: compareId !== NONE_VALUE,
  })
  const { data: segmentGroups } = useSegmentGroupsQuery()

  const { getSegmentGroupTranslation } = usePredefinedSegmentGroupsTranslation()

  const shouldShowRfmGrid =
    segmentGroup?.isPredefined &&
    segmentGroup.name === PredefinedSegmentGroups.rfm &&
    (!compareId || compareId === NONE_VALUE)

  // Find the comparison segment group for translations
  const comparisonSegmentGroup = useMemo(() => {
    return segmentGroups?.find((sg) => sg.id === compareId)
  }, [segmentGroups, compareId])

  return (
    <>
      <div className="flex flex-row w-full items-start justify-between gap-4">
        <Card className="p-6 flex flex-col gap-4 w-full">
          <div className="flex flex-row items-start justify-between">
            <div className="flex flex-col">
              <h2 className="text-lg font-medium">
                {segmentGroup && getSegmentGroupTranslation(segmentGroup)}
              </h2>
              {segmentGroup?.name && segmentGroup.isPredefined && (
                <span className="text-sm text-muted-foreground">
                  {t(`segments.predefined.kind.${segmentGroup.name}.description`)}
                </span>
              )}
            </div>
            {segmentGroup?.isPredefined && (
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="secondary" className="w-fit">
                    {t("segments.predefined.label")}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{t("segments.predefined.tooltip")}</TooltipContent>
              </Tooltip>
            )}
          </div>
          <SegmentGroupMetrics segmentGroupId={segmentGroupId} />
        </Card>
      </div>
      <Card className="flex flex-col gap-8 w-full p-6">
        <div className="flex sm:flex-row flex-col gap-4 items-start justify-between">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-0.5">
              <span className="text-lg font-medium">{t("segments.distribution.title")}</span>
              <span className="text-sm text-muted-foreground">
                {t("segments.distribution.description")}
              </span>
            </div>
          </div>
          <div className="flex flex-row gap-3">
            <VisualizationSelect value={view} onChange={onChangeView} />
            <SegmentGroupCompareSelect
              segmentGroupId={segmentGroupId}
              selectedSegmentGroupId={compareId ?? ""}
              onValueChange={onChangeCompareId}
              noneValue={NONE_VALUE}
              disabled={view === "table"}
            />
          </div>
        </div>
        {view === "chart" && shouldShowRfmGrid && (
          <RfmGrid segmentGroup={segmentGroup} isPending={isPending} />
        )}
        {view === "chart" && !shouldShowRfmGrid && (
          <SegmentGroupChart
            segmentGroup={segmentGroup}
            comparisonData={compareId !== NONE_VALUE ? segmentGroupCompare : undefined}
            comparisonSegmentGroup={comparisonSegmentGroup}
            isPending={isPending}
          />
        )}
        {view === "table" && (
          <SegmentsTable
            segments={segmentGroup?.segments ?? []}
            segmentGroup={segmentGroup!}
            isPending={isPending}
            totalCustomers={segmentGroup?.customersCount ?? 0}
          />
        )}
      </Card>
    </>
  )
}
